/* import __COLOCATED_TEMPLATE__ from './conversation-insights.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { DeduplicatedAsyncData } from 'embercom/resources/utils/async-data';
import type Conversation from 'embercom/objects/inbox/conversation';
import type ConversationTopic from 'embercom/objects/inbox/conversation-topic';
import type ConversationalInsightsApi from 'embercom/services/inbox2-conversational-insights-api';
import type SidebarSection from 'embercom/objects/inbox/sidebar-section';
import { type SidebarSectionId } from 'embercom/components/inbox2/conversation-details-sidebar/sidebar-item-container';

interface Args {
  conversation: Conversation;
  name: string;
  isOpen: boolean;
  onOpenSectionChange: (value: SidebarSectionId) => (value: SidebarSectionId) => unknown;
  sidebarSection: SidebarSection;
}

interface Signature {
  Args: Args;
}

export default class ConversationInsights extends Component<Signature> {
  @service declare inbox2ConversationalInsightsApi: ConversationalInsightsApi;

  private topicsLoader = DeduplicatedAsyncData(
    this,
    () => [this.args.conversation.id],
    (conversationId, { signal }) => {
      return this.inbox2ConversationalInsightsApi.fetchTopicsForConversation(conversationId, {
        signal,
      });
    },
  );

  get topics(): ConversationTopic[] {
    return this.topicsLoader.value || [];
  }

  get isLoadingTopics(): boolean {
    return this.topicsLoader.isLoading;
  }

  get cxScore(): string | number | undefined {
    return this.args.conversation.attributesById?.['customer_experience_rating']?.value as
      | string
      | number
      | undefined;
  }

  get cxExplanation(): string | undefined {
    return this.args.conversation.attributesById?.['customer_experience_explanation']?.value as
      | string
      | undefined;
  }

  get hasCxData(): boolean {
    return this.cxScore !== undefined && this.cxScore !== null;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationDetailsSidebar::SidebarSections::ConversationInsights': typeof ConversationInsights;
    'inbox2/conversation-details-sidebar/sidebar-sections/conversation-insights': typeof ConversationInsights;
  }
}
